<template>
  <AppDialog
    v-model="isVisible"
    :modal="true"
    :draggable="false"
    :closable="false"
    width="min(100vw, 48rem)"
    custom-class="w-full max-w-3xl backdrop-blur-xl"
  >
    <!--  -->
    <div class="flex flex-col gap-4">
      <ReplyPreview
        v-if="replyToMessage"
        :message="replyToMessage"
        @close="$emit('cancel-reply')"
      />

      <div ref="formatToolbarRef" class="flex items-center justify-between gap-1 px-2">
        <FormatToolbar
          :is-signature-enabled="isSignatureEnabled"
          :message-text="localMessageText"
          :active-formats="activeFormats"
          @format-bold="formatBold"
          @format-italic="formatItalic"
          @format-strikethrough="formatStrikethrough"
          @format-monospace="formatMonospace"
          @toggle-signature="$emit('toggle-signature')"
          @open-quick-replies="toggleQuickReplies"
          @open-ai-menu="toggleAIMenu"
        />

        <div>
          <Button
            ref="fileUploadButtonRef"
            text
            rounded
            size="small"
            class="p-1! text-[var(--p-surface-500)]"
            @click="toggleFileUpload"
            v-tooltip.top="$t('whatsapp.actions.attach_files')"
          >
            <template #icon>
              <IconPlus
                width="16"
                height="16"
                class="text-[var(--p-primary-color)] dark:text-[var(--p-primary-50)]"
              />
            </template>
          </Button>
          <Button
            ref="quickRepliesButtonRef"
            text
            rounded
            size="small"
            class="p-1! text-[var(--p-surface-500)]"
            @click="toggleQuickReplies"
            v-tooltip.top="$t('whatsapp.actions.fast_responses')"
          >
            <template #icon>
              <IconStair
                width="16"
                height="16"
                class="text-[var(--p-primary-color)] dark:text-[var(--p-primary-50)]"
              />
            </template>
          </Button>

          <Popover
            ref="fileUploadPanel"
            @show="focusFirstPopoverControl(fileUploadPanel)"
            class="dark:!bg-[var(--p-primary-950)] dark:!border-[var(--p-primary-400)]/30 !backdrop-bur-2xl"
          >
            <FileUploadMenu
              @files-select="handleFilesSelect"
              @close="closeFileUpload"
              @open-camera="handleOpenCamera"
            />
          </Popover>

          <Button
            text
            rounded
            size="small"
            :severity="isSignatureEnabled ? 'primary' : 'secondary'"
            class="p-1!"
            @click="$emit('toggle-signature')"
            v-tooltip.top="$t('whatsapp.actions.signature')"
          >
            <template #icon>
              <IconSignature
                width="16"
                height="16"
                class="text-[var(--p-primary-color)] dark:text-[var(--p-primary-50)]"
              />
            </template>
          </Button>
        </div>
      </div>

      <div class="relative">
        <div
          class="flex flex-col border border-[var(--p-primary-200)]/80 dark:border-[var(--p-primary-color)]/10 gap-2 p-4 bg-[var(--p-surface-100)] dark:bg-[var(--p-primary-950)]/30 backdrop-blur-xl rounded-lg"
        >
          <div class="flex items-start gap-2">
            <Button
              ref="emojiButtonRef"
              text
              rounded
              size="small"
              class="flex-shrink-0 text-[var(--p-surface-500)] p-1!"
              @click.stop="toggleEmojiPicker"
              v-tooltip.top="$t('whatsapp.actions.select_emojis')"
            >
              <template #icon>
                <IconEmoji width="18" height="18" class="dark:text-[var(--p-primary-50)]" />
              </template>
            </Button>
            <EmojiPicker
              ref="emojiPanel"
              hide-trigger
              position="right"
              @emoji="handleEmojiSelect"
            />

            <div
              ref="expandedMessageInput"
              contenteditable="true"
              role="textbox"
              aria-multiline="true"
              :data-placeholder="$t('whatsapp.message_input.placeholder')"
              class="rich-message-editor flex-1 bg-transparent border-0 outline-none text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)] text-sm px-2 py-1 min-h-[250px] max-h-[400px] overflow-y-auto leading-relaxed whitespace-pre-wrap break-words"
              @keydown="handleKeydown"
              @input="syncTextFromEditor"
              @paste="handlePaste"
              @keyup="updateActiveFormats"
              @mouseup="updateActiveFormats"
            ></div>

            <div class="flex flex-shrink-0 flex-col items-center gap-1">
              <TextAIAssistant
                ref="aiAssistantRef"
                :model-value="localMessageText"
                field-type="textarea"
                :title="$t('whatsapp.actions.ia_asist')"
                v-tooltip.top="$t('whatsapp.actions.ia_asist')"
                panel-mode="fixed"
                :embedded="false"
                @replace="handleAIReplace"
                @append="handleAIAppend"
              />
            </div>
          </div>

          <div
            v-if="isSignatureEnabled"
            class="flex items-center gap-2 px-3 py-2 bg-[var(--p-primary-50)] dark:bg-[var(--p-primary-900)]/20 border-l-2 border-[var(--p-primary-color)] rounded text-xs"
          >
            <IconSignature
              width="14"
              height="14"
              class="text-[var(--p-primary-color)] dark:text-[var(--p-primary-100)] flex-shrink-0"
            />
            <span class="text-[var(--p-surface-600)] dark:text-[var(--p-primary-100)]">
              {{ $t('whatsapp.message_input.messages_will_include') }}:
              <span class="font-medium italic">> {{ userName }}</span>
            </span>
          </div>
        </div>

        <div
          class="text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-0)]/90 mt-2 flex items-center justify-between"
        >
          <span>{{ $t('whatsapp.expanded_message_input.send_shortcut') }}</span>
          <span>
            {{
              $t('whatsapp.expanded_message_input.character_count', {
                count: localMessageText.length,
              })
            }}
          </span>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <AppButtonIcon
          :label="$t('general.cancel')"
          severity="secondary"
          class="w-1/4!"
          text
          size="small"
          @click="handleClose"
        />
        <AppButtonIcon
          :label="$t('whatsapp.expanded_message_input.send_message')"
          severity="primary"
          icon-pos="right"
          size="small"
          :disabled="!localMessageText.trim()"
          @click="handleSend"
        >
          <template #icon>
            <SendHorizonal :size="16" />
          </template>
        </AppButtonIcon>
      </div>
    </template>
    <Popover
      ref="quickRepliesPanel"
      :dismissable="false"
      @show="focusFirstPopoverControl(quickRepliesPanel)"
      class="dark:!bg-[var(--p-primary-950)] dark:!border-[var(--p-primary-400)]/30 !backdrop-bur-2xl"
    >
      <QuickReplies @select-reply="handleQuickReply" />
    </Popover>
  </AppDialog>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
import AppDialog from '@/components/atoms/dialogs/AppDialog.vue'
import Button from 'primevue/button'
import Popover from 'primevue/popover'
import { Send, SendHorizonal } from 'lucide-vue-next'
import type { Message } from '../../types/interfaces'
import FormatToolbar from './FormatToolbar.vue'
import EmojiPicker from '@/components/atoms/emoji/EmojiPicker.vue'
import FileUploadMenu from '../../molecules/file-upload/FileUploadMenu.vue'
import QuickReplies from '../../molecules/quick-replies/QuickReplies.vue'
import ReplyPreview from '../../molecules/message/ReplyPreview.vue'
import TextAIAssistant from '@/components/organism/textAiAssistant/TextAIAssistant.vue'
import { extractImageFilesFromClipboard } from '@/components/whatsapp/composables/useClipboardImages'
import { useRichWhatsappEditor } from '@/components/whatsapp/composables/useRichWhatsappEditor'
import type { FilePreviewCategory } from '@/components/whatsapp/composables/useFiles'
import IconPlus from '@/assets/svg/plus_icon.svg?component'
import IconEmoji from '@/assets/svg/emoji_icon.svg?component'
import IconStair from '@/assets/svg/stair_icon.svg?component'
import IconSignature from '@/assets/svg/signature_icon.svg?component'
import AppButtonIcon from '@/components/atoms/buttons/AppButtonIcon.vue'

interface Props {
  visible: boolean
  messageText: string
  isSignatureEnabled: boolean
  userName: string
  replyToMessage?: Message | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
  'update:message-text': [value: string]
  'send-message': []
  'toggle-signature': []
  'files-select': [files: File[], category?: FilePreviewCategory]
  'open-camera': []
  'cancel-reply': []
}>()

type TextAIAssistantExpose = {
  toggleAssistant: () => void
}

const isVisible = ref(props.visible)
const localMessageText = ref(props.messageText)
const {
  editorRef: expandedMessageInput,
  activeFormats,
  syncEditorFromText,
  syncTextFromEditor,
  focusEditor,
  toggleFormat,
  updateActiveFormats,
  insertText,
  handlePaste: handleEditorPaste,
  handleFormatKeydown,
} = useRichWhatsappEditor(localMessageText)
const emojiPanel = ref()
const quickRepliesPanel = ref()
const fileUploadPanel = ref()
const emojiButtonRef = ref()
const fileUploadButtonRef = ref()
const quickRepliesButtonRef = ref()
const formatToolbarRef = ref<HTMLElement | null>(null)
const aiAssistantRef = ref<TextAIAssistantExpose | null>(null)

watch(
  () => props.visible,
  (newVal) => {
    isVisible.value = newVal
    if (newVal) {
      localMessageText.value = props.messageText
      nextTick(() => {
        syncEditorFromText()
        focusEditor()
      })
    }
  },
)

watch(
  () => props.messageText,
  (newVal) => {
    localMessageText.value = newVal
    nextTick(syncEditorFromText)
  },
)

watch(isVisible, (newVal) => {
  emit('update:visible', newVal)
  if (!newVal) {
    emit('update:message-text', localMessageText.value)
  }
})

watch(localMessageText, (newVal) => {
  emit('update:message-text', newVal)
  nextTick(syncEditorFromText)
})

const formatBold = () => toggleFormat('bold')
const formatItalic = () => toggleFormat('italic')
const formatStrikethrough = () => toggleFormat('strikethrough')
const formatMonospace = () => toggleFormat('monospace')

const getComponentElement = (componentRef: unknown) => {
  if (componentRef instanceof HTMLElement) return componentRef
  if (
    componentRef &&
    typeof componentRef === 'object' &&
    '$el' in componentRef &&
    componentRef.$el instanceof HTMLElement
  ) {
    return componentRef.$el
  }
  return undefined
}

const createAnchoredPopoverEvent = (event: Event, target?: HTMLElement) => {
  if (!target) return event

  return {
    ...event,
    currentTarget: target,
    target,
  } as unknown as Event
}

const getPopoverContainer = (popoverRef: unknown) => {
  const candidate =
    popoverRef && typeof popoverRef === 'object' && 'value' in popoverRef
      ? popoverRef.value
      : popoverRef

  if (
    candidate &&
    typeof candidate === 'object' &&
    'container' in candidate &&
    candidate.container instanceof HTMLElement
  ) {
    return candidate.container
  }

  return getComponentElement(candidate)
}

const focusFirstPopoverControl = (popoverRef: unknown) => {
  nextTick(() => {
    requestAnimationFrame(() => {
      const popover = getPopoverContainer(popoverRef)
      const focusable = popover?.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )

      focusable?.focus()
    })
  })
}

const isInsideElement = (target: EventTarget | null, element?: HTMLElement) =>
  Boolean(target instanceof Node && element?.contains(target))

const isQuickRepliesShortcutDialogOpen = () =>
  Boolean(document.querySelector('.quick-replies-shortcut-dialog'))

const isInsideQuickRepliesOwnedOverlay = (target: EventTarget | null) =>
  Boolean(
    target instanceof Element &&
      (target.closest('.quick-replies-shortcut-dialog') ||
        target.closest('.text-ai-assistant-panel') ||
        (isQuickRepliesShortcutDialogOpen() && target.closest('[data-emoji-picker]')) ||
        (isQuickRepliesShortcutDialogOpen() && target.closest('.p-dialog-mask'))),
  )

const handleQuickRepliesOutsideClick = (event: MouseEvent) => {
  const target = event.target
  const quickRepliesContainer = getPopoverContainer(quickRepliesPanel.value)
  const quickRepliesButton = getComponentElement(quickRepliesButtonRef.value)

  if (!quickRepliesContainer) return
  if (isInsideElement(target, quickRepliesContainer)) return
  if (isInsideElement(target, quickRepliesButton)) return
  if (isInsideQuickRepliesOwnedOverlay(target)) return

  quickRepliesPanel.value?.hide()
}

const toggleEmojiPicker = (event: Event, target?: HTMLElement) => {
  emojiPanel.value?.togglePickerFromElement(target ?? getComponentElement(emojiButtonRef.value))
}

const toggleAIMenu = (event: Event, target?: HTMLElement) => {
  aiAssistantRef.value?.toggleAssistant()
}

const toggleQuickReplies = (event: Event, target?: HTMLElement) => {
  quickRepliesPanel.value?.toggle(createAnchoredPopoverEvent(event, target), target)
}

const toggleFileUpload = (event: Event, target?: HTMLElement) => {
  fileUploadPanel.value?.toggle(createAnchoredPopoverEvent(event, target), target)
}

const toggleEmojiPickerShortcut = (event: Event) => {
  toggleEmojiPicker(event, getComponentElement(emojiButtonRef.value))
  focusFirstPopoverControl(emojiPanel.value)
}

const toggleFileUploadShortcut = (event: Event) => {
  toggleFileUpload(event, getComponentElement(fileUploadButtonRef.value))
  focusFirstPopoverControl(fileUploadPanel.value)
}

const toggleQuickRepliesShortcut = (event: Event) => {
  toggleQuickReplies(event, getComponentElement(quickRepliesButtonRef.value))
  focusFirstPopoverControl(quickRepliesPanel.value)
}

const toggleAIMenuShortcut = (event: Event) => {
  aiAssistantRef.value?.toggleAssistant()
}

const handleEmojiSelect = (emoji: string) => {
  insertText(emoji)
  focusEditor()
}

const handleQuickReply = (reply: string) => {
  localMessageText.value = reply
  quickRepliesPanel.value?.hide()
  nextTick(() => {
    syncEditorFromText()
    focusEditor()
  })
}

const syncTextAndEditor = (text: string) => {
  localMessageText.value = text
  nextTick(() => {
    syncEditorFromText()
    focusEditor()
  })
}

const handleAIReplace = (text: string) => {
  syncTextAndEditor(text)
}

const handleAIAppend = (text: string) => {
  syncTextAndEditor(localMessageText.value ? `${localMessageText.value}\n${text}` : text)
}

const handleOpenCamera = () => {
  closeFileUpload()
  isVisible.value = false
  emit('open-camera')
}

const handleFilesSelect = (files: File[], category?: FilePreviewCategory) => {
  closeFileUpload()
  isVisible.value = false
  emit('files-select', files, category)
}

const handlePaste = (event: ClipboardEvent) => {
  const images = extractImageFilesFromClipboard(event)
  if (images.length > 0) {
    event.preventDefault()
    isVisible.value = false
    emit('files-select', images, 'image')
    return
  }

  handleEditorPaste(event)
}

const handleKeydown = (event: KeyboardEvent) => {
  if (handleFormatKeydown(event)) return

  if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
    event.preventDefault()
    handleSend()
  }
}

const closeFileUpload = () => {
  fileUploadPanel.value?.hide()
}

const handleSend = () => {
  if (localMessageText.value.trim()) {
    emit('send-message')
    isVisible.value = false
  }
}

const handleClose = () => {
  isVisible.value = false
}

onMounted(() => {
  document.addEventListener('mousedown', handleQuickRepliesOutsideClick, true)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', handleQuickRepliesOutsideClick, true)
})

defineExpose({
  toggleEmojiPickerShortcut,
  toggleFileUploadShortcut,
  toggleQuickRepliesShortcut,
  toggleAIMenuShortcut,
})
</script>

<style scoped>
.rich-message-editor:empty::before {
  content: attr(data-placeholder);
  color: var(--p-surface-400);
  pointer-events: none;
}

.rich-message-editor :deep(code),
.rich-message-editor :deep(font[face*='monospace']) {
  border-radius: 0.25rem;
  background: rgb(0 0 0 / 0.08);
  padding: 0.0625rem 0.25rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
}

:global(.dark) .rich-message-editor :deep(code),
:global(.dark) .rich-message-editor :deep(font[face*='monospace']) {
  background: rgb(255 255 255 / 0.12);
}
</style>
